import { useEffect, useMemo, useState } from 'react';
import { useLocation, useMatch, useParams } from 'react-router-dom';
import useTimelinePaging from './paging/timelinePagingHelper';
import {
  useListCrud,
  useListDataLoading,
  useListLocalStorageStoring,
  useListParams,
  useListParamsConcatenation,
  useListUrlStoring,
  getLoadingParams,
  Filter,
} from 'framework/list';
import { UrlParams } from 'framework/routing';
import { TimelineItem, TimelineItemId, TimelineResponseData, TimelineEntity, TimelineRegistry } from './types';
import { ResourceData, ResourceId } from 'framework/crud';

const pageSize = 30;

interface TimeLineHelperParams {
  getDataMethod: (request?: any) => Promise<TimelineResponseData>;
  registries: TimelineRegistry[];
  createTimelineItemMethod?: (itemType: string, data: ResourceData) => Nullable<Promise<TimelineItem>>;
  getPageByEntryIdMethod?: (id: StringOrNumber) => Promise<TimelineResponseData>;
  pinTimelineAction?: (entityId: ResourceId, timelineId: ResourceId, pinStatus: boolean) => Promise<any>;
  useWindow?: boolean;
  scrollableContainer?: React.RefObject<HTMLElement>;
  defaultSortOrder?: string;
  defaultSortField?: string;
  timelineEntryIdSource: string;
  defaultFilter?: Filter;
}

const useTimeline = ({
  getDataMethod,
  getPageByEntryIdMethod,
  registries,
  createTimelineItemMethod,
  timelineEntryIdSource,
  defaultSortOrder = 'DESC',
  defaultSortField = 'createdAt',
  defaultFilter = {},
  pinTimelineAction,
  useWindow = true,
  scrollableContainer,
}: TimeLineHelperParams) => {
  const location = useLocation();

  const match = useMatch(location.pathname);

  const path = match ? match.pattern.path : null;

  const [currentEditingId, setCurrentEditingId] = useState<TimelineItemId>(null);
  const [addForm, setAddForm] = useState<Nullable<React.ReactElement>>(null);
  const timelineUrlParams: UrlParams = useParams<Dictionary<string>>();
  const timelineEntryId = timelineUrlParams[timelineEntryIdSource];

  const isEntryViewMode = timelineEntryId && !!getPageByEntryIdMethod;

  const { params: urlParams, updateParamsInUrl } = useListUrlStoring();
  const { params: localStorageParams, updateParamsInLocalStorage } = useListLocalStorageStoring({
    localStorageKey: path,
  });
  const { params: initialParams } = useListParamsConcatenation({ urlParams, localStorageParams });
  const { params, goToPage, filterBy, resetParamsToDefault } = useListParams({
    initialParams,
    defaultSortOrder,
    defaultSortField,
    defaultFilter,
    onParamsChangeCallbacks: [updateParamsInUrl, updateParamsInLocalStorage],
  });

  const {
    loading,
    setLoading: setGlobalLoading,
    loadData: _loadData,
  } = useListDataLoading({
    getDataMethod,
    resetParamsToDefault,
  });
  const { data, setData, updateInList: _updateInList, addToList, deleteFromList } = useListCrud();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadingParams = useMemo(() => getLoadingParams(params, pageSize), [params.filter, params.sort]);

  const countPinElements = useMemo(() => data?.items?.filter(item => item.isPinned).length || null, [data.items]);

  const { resetPaging, ...pagingMethods } = useTimelinePaging({
    currentPage: params.page || 0,
    loadData: (pageNumber: number) => _loadData({ ...loadingParams, pageNumber }, false),
    setData,
    data,
    loadingParams,
    useWindow,
    scrollableContainer,
  });

  const handleLoading = async (loadDataMethod: () => Promise<TimelineResponseData>) => {
    setGlobalLoading(true);
    const response = await loadDataMethod();
    setData({
      items: response?.result || [],
      totalPages: response?.totalPages || 0,
    });
    goToPage(response?.pageNumber || 0);
    setGlobalLoading(false);
  };

  const loadData = () => {
    resetPaging!();
    if (timelineEntryId && getPageByEntryIdMethod) {
      return handleLoading(() => getPageByEntryIdMethod(timelineEntryId));
    } else {
      return handleLoading(() => _loadData(loadingParams, false));
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingParams]);

  /**
   * Wraps item's object to timeline entry's object
   */
  const wrapItem = (item: TimelineItem, source: string): TimelineEntity => {
    const mainData = {
      id: item?.timelineId,
      authorId: item?.createdById,
      updatedAt: item?.updatedAt,
      createdAt: item?.createdAt,
      isPinned: item?.isPinned,
      artifacts: item?.artifacts || [],
    };

    if (!source) {
      return {
        ...mainData,
        ...item,
      };
    }

    return {
      [source]: item,
      ...mainData,
    };
  };

  const updateInList = (id: TimelineItemId, newEntity: TimelineItem) => {
    _updateInList(newEntity);
  };

  const setCurrentEditingIdMethod = (id: TimelineItemId) => {
    setCurrentEditingId(id);
    setAddForm(null);
  };

  const setAddFormMethod = (form: Nullable<React.ReactElement>) => {
    setAddForm(form);
    setCurrentEditingId(null);
  };

  const isHighlighted = (id: TimelineItemId) => {
    return !!id && String(id) === String(timelineEntryId);
  };

  return {
    resetPaging,
    ...pagingMethods,
    filter: params.filter,
    sort: params.sort,
    filterBy,
    isEntryViewMode,
    data,
    setCurrentEditingId: setCurrentEditingIdMethod,
    currentEditingId,
    setAddForm: setAddFormMethod,
    addForm,
    registries,
    wrapItem,
    createTimelineItemMethod,
    isHighlighted,
    loading,
    addToList,
    deleteFromList,
    updateInList,
    reloadData: loadData,
    pinTimelineAction,
    countPinElements,
    scrollableContainer,
  };
};

export default useTimeline;
